import { PlusCircleIcon } from '@heroicons/react/24/outline'
import { TrashIcon } from '@heroicons/react/24/solid'
import { AxiosError } from 'axios'
import { FC, useCallback, useEffect } from 'react'
import {
  Controller,
  ControllerRenderProps,
  useFieldArray,
  useFormContext
} from 'react-hook-form'

import { Auto, joinAutoNumber } from '@/api/models/parking'
import { RequestTypeAlias } from '@/api/models/requests/lib/requests.types'
import { ServerValidationError } from '@/api/shared/types'
import { YandexMetrikaEvent } from '@/app/constants/metrics'
import { AutoNumberInput } from '@/components/entities/passes/ui/PassForms/FormBlock/AutoMasked/AutoNumberInput'
import { useParkingPlaces } from '@/components/entities/requests/hooks/useParkingPlaces'
import { AppInput } from '@/components/shared/input'
import AppInputMessage from '@/components/shared/input/ui/AppInputMessage'
import { AppSelect, AppSelectOption } from '@/components/shared/select'
import { useYandexMetrika } from '@/components/shared/yandex-metrika/hooks/useYandexMetrika'
import {
  hasAutoNumberServerError,
  parseServerErrorField
} from '@/utils/parseServerErrorField'

import {
  DEFAULT_AUTO_MODEL_MAX_LENGTH,
  DEFAULT_AUTO_MODEL_MAX_LENGTH_MESSAGE_FROM
} from '../../../../requests/model/constants'
import {
  AutosFormData,
  AutosFormErrors
} from '../../../../requests/ui/model/schemas'
import { ConfirmDeleteModal } from './ConfirmDeleteModal'
import { PassQuickAutos } from './QuickAutos/PassQuickAutos'
import { useConfirmDelete } from './useConfirmDelete'

type Props = {
  typeAlias: RequestTypeAlias
  passError?: AxiosError<ServerValidationError> | null
  singleCarMode?: boolean
}

const PassAutosFormBlock: FC<Props> = ({
  typeAlias,
  passError,
  singleCarMode = false
}) => {
  const { parkingPlaces } = useParkingPlaces()
  const { reachGoal } = useYandexMetrika()

  const parkingOptions: AppSelectOption[] = parkingPlaces.map(place => ({
    id: place.id,
    name: place.value
  }))

  const shouldHideParkingField =
    typeAlias === RequestTypeAlias.RentPassAuto ||
    typeAlias === RequestTypeAlias.PropertiesPass
  const shouldShowCarModelField = typeAlias !== RequestTypeAlias.PropertiesPass

  const {
    control,
    formState: { errors },
    clearErrors,
    setError,
    setValue,
    getValues
  } = useFormContext<AutosFormData, AutosFormErrors>()

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'autos'
  })

  const {
    opened: isDeleteModalOpened,
    requestDelete,
    onClose: handleDeleteCancel,
    onConfirm: handleDeleteConfirm
  } = useConfirmDelete(remove, index => {
    const auto = getValues(`autos.${index}`)
    return Boolean(auto?.number?.trim() || auto?.model?.trim())
  })

  const isMultipleFields = fields.length > 1

  const applyAutosServerErrors = useCallback(() => {
    const serverErrors = passError?.response?.data?.errors
    if (!serverErrors?.length) {
      return
    }

    serverErrors.forEach(error => {
      const isAutoNumberError = error.field === 'autoNumber'

      if (!isAutoNumberError && !hasAutoNumberServerError(error.field)) {
        return
      }

      const index = parseServerErrorField(error.field)

      if (index === null && !isAutoNumberError) {
        return
      }

      const targetIndex = index ?? (singleCarMode ? 0 : null)

      if (error.message && targetIndex !== null) {
        setError(`autos.${targetIndex}.number`, {
          type: 'server',
          message: error.message
        })
      }
    })
  }, [passError, setError, singleCarMode])

  useEffect(() => {
    applyAutosServerErrors()
  }, [applyAutosServerErrors])

  const handleAddCar = () => {
    append({
      number: '',
      model: '',
      parkingPlace: shouldHideParkingField ? null : parkingPlaces[0] ?? null,
      isStandard: true
    })
  }

  const handleQuickAutoSelect = (index: number, auto: Auto) => {
    reachGoal(YandexMetrikaEvent.PASSES_AUTO_QUICK_ADD)

    clearErrors(`autos.${index}.number`)

    setValue(
      `autos.${index}.number`,
      joinAutoNumber(auto.number, auto.region),
      {
        shouldDirty: true
      }
    )
    setValue(`autos.${index}.isStandard`, auto.isStandard, {
      shouldDirty: true
    })

    if (shouldShowCarModelField) {
      setValue(`autos.${index}.model`, auto.model ?? '', { shouldDirty: true })
    }
  }

  const handleParkingChange = (
    field: ControllerRenderProps<AutosFormData, `autos.${number}.parkingPlace`>,
    option: AppSelectOption
  ) => {
    const selectedParking = parkingPlaces.find(place => place.id === option.id)
    field.onChange(selectedParking || null)
  }

  return (
    <>
      {fields.map((field, index) => {
        const isStandard = getValues(`autos.${index}.isStandard`) ?? true

        return (
          <div key={field.id} className="form-block-cover my-16 p-32 lg:p-16">
            <div className="mb-16 flex justify-between">
              <p className="form-block-title">
                Транспортное средство {isMultipleFields && index + 1}
              </p>
              {isMultipleFields && (
                <button
                  aria-label="Удалить транспортное средство из списка"
                  className="flex items-center justify-center"
                  onClick={() => requestDelete(index)}
                  type="button"
                >
                  <TrashIcon className="h-28 w-28 text-grey-60 xs:h-24 xs:w-24" />
                </button>
              )}
            </div>
            <div className="mb-16 flex items-start gap-12 xs:flex-col xs:gap-16">
              <div className="flex-0 w-[260px] sm:w-full">
                <Controller
                  name={`autos.${index}.number`}
                  control={control}
                  defaultValue={field.number}
                  render={({ field }) => (
                    <AutoNumberInput
                      value={field.value}
                      isStandard={isStandard}
                      dataTestId={`pass-auto-number-input-${index + 1}-id`}
                      onStandardChange={isStandard =>
                        setValue(`autos.${index}.isStandard`, isStandard, {
                          shouldDirty: true
                        })
                      }
                      onChange={(value, skipValidation) => {
                        if (skipValidation) {
                          clearErrors(`autos.${index}.number`)
                          setValue(field.name, value, { shouldValidate: false })
                        } else {
                          clearErrors(`autos.${index}.number`)
                          field.onChange(value)
                        }
                      }}
                      onBlur={field.onBlur}
                      invalid={Boolean(errors.autos?.[index]?.number)}
                      invalidMessage={errors.autos?.[index]?.number?.message}
                    />
                  )}
                />
              </div>
              {shouldShowCarModelField && (
                <div className="flex-1 self-start xs:w-full">
                  <Controller
                    name={`autos.${index}.model`}
                    control={control}
                    defaultValue={field.model}
                    render={({ field }) => (
                      <AppInput
                        {...field}
                        dataTestId={`pass-auto-model-input-${index + 1}-id`}
                        autoComplete="off"
                        placeholder="Марка ТС"
                        invalid={Boolean(errors.autos?.[index]?.model)}
                        invalidMessage={errors.autos?.[index]?.model?.message}
                        className="w-full border border-grey-20"
                        maxLength={DEFAULT_AUTO_MODEL_MAX_LENGTH}
                        maxLengthMessageFrom={
                          DEFAULT_AUTO_MODEL_MAX_LENGTH_MESSAGE_FROM
                        }
                        onChange={event => {
                          field.onChange(event)
                        }}
                      />
                    )}
                  />
                </div>
              )}
            </div>
            <PassQuickAutos
              className="mb-16"
              onSelect={auto => handleQuickAutoSelect(index, auto)}
            />

            {!shouldHideParkingField && (
              <div className="mb-24 md:mb-0">
                <Controller
                  name={`autos.${index}.parkingPlace`}
                  control={control}
                  defaultValue={field.parkingPlace}
                  render={({ field }) => {
                    const selectedOption = parkingOptions.find(
                      option => option.id === field.value?.id
                    )

                    return (
                      <AppSelect
                        label="Паркинг"
                        options={parkingOptions}
                        selectedOption={selectedOption}
                        onChange={(option: AppSelectOption) =>
                          handleParkingChange(field, option)
                        }
                        isError={Boolean(errors.autos?.[index]?.parkingPlace)}
                      />
                    )
                  }}
                />
                {errors.autos?.[index]?.parkingPlace?.message && (
                  <AppInputMessage
                    message={errors.autos?.[index]?.parkingPlace?.message}
                  />
                )}
              </div>
            )}
          </div>
        )
      })}

      {!singleCarMode && (
        <button
          className="mb-30 flex items-center gap-x-4"
          onClick={handleAddCar}
          type="button"
        >
          <PlusCircleIcon className="h-20 w-20 text-bronze-40" />
          <p className="font-bold text-bronze-40 text-16">Добавить ТС</p>
        </button>
      )}

      <ConfirmDeleteModal
        opened={isDeleteModalOpened}
        header="Удалить транспортное средство?"
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </>
  )
}

export { PassAutosFormBlock }
